import { useViewerLocale } from "@/lib/translator";
import formatPrice from "@/lib/utils/format-price";
import { getPaidPrice } from "@/utils/return-requests";
import useFormattedDate from "@/dates/use-formatted-date";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";

/** Reminds the merchant which return they are turning down, so a modal opened
 * from a list is never ambiguous. */
export function ReturnSummary({
  returnRequest,
}: {
  returnRequest: ReturnRequestDto;
}) {
  const t = useTranslation();
  const locale = useViewerLocale();
  const { relativeDate } = useFormattedDate(returnRequest.createdAt);

  const items = returnRequest.line_items;
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  // The same helper as the financial card below: raw `price` ignores
  // non-included taxes, allocated discounts and the exchange-origin amount, and
  // the two figures would contradict each other on the same page.
  const total = items.reduce(
    (sum, item) => sum + getPaidPrice(item) * item.quantity,
    0,
  );
  const currency = returnRequest.currency.customerCurrency;
  const thumbnail = items.find((item) => item.image)?.image;

  const customerName = [
    returnRequest.customer.first_name,
    returnRequest.customer.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex items-center gap-3">
      {thumbnail && (
        <img
          src={thumbnail}
          alt=""
          className="size-10 shrink-0 rounded-md border border-border object-cover"
        />
      )}
      <div className="flex min-w-0 flex-col">
        <p className="truncate text-sm font-medium">
          {returnRequest.orderName}
          {customerName && ` · ${customerName}`}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {t("disapproveReturn.summary.items", { count: itemCount })}
          {` · ${formatPrice({ amount: total, currency, locale })}`}
          {` · ${relativeDate}`}
        </p>
      </div>
    </div>
  );
}
